/**
 * UserAvailabilitySection component
 * Displays and manages availability for a single user
 */

import { useState, useEffect } from 'react';
import { addDays } from 'date-fns';
import { WeekCalendar } from './WeekCalendar';
import { useAvailability, useBulkUpdateAvailability } from '@/hooks/useAvailability';
import { getWeekRangeForAPI, formatDateForAPI } from '@/lib/utils/date';
import type { BulkAvailabilityItem, User } from '@/types/api';
import { useTranslation } from 'react-i18next';

interface UserAvailabilitySectionProps {
  user: User;
  weekStart: Date;
  isOwnAvailability: boolean;
}

export function UserAvailabilitySection({ user, weekStart, isOwnAvailability }: UserAvailabilitySectionProps) {
  const { t } = useTranslation();
  const [localAvailability, setLocalAvailability] = useState<BulkAvailabilityItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const { startDate, endDate } = getWeekRangeForAPI(weekStart);

  // Fetch availability for current week
  const { data, isLoading } = useAvailability({
    userId: user.id,
    startDate,
    endDate,
  });

  // Bulk update mutation
  const bulkUpdateMutation = useBulkUpdateAvailability();

  // Initialize local state from fetched data with smart defaults for missing days
  useEffect(() => {
    if (data?.availability) {
      const fetchedData = data.availability;
      const mergedAvailability: BulkAvailabilityItem[] = [];

      // Generate all dates for the week with smart defaults
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStart, i);
        const dateString = formatDateForAPI(date);

        // Use user's default availability preference
        const defaultPresent = user.defaultAvailability || false;

        // Check if lunch data exists for this date
        const lunchData = fetchedData.find(
          (a) => a.date === dateString && a.mealType === 'lunch'
        );
        mergedAvailability.push({
          date: dateString,
          mealType: 'lunch' as const,
          isPresent: lunchData ? lunchData.isPresent : defaultPresent,
        });

        // Check if dinner data exists for this date
        const dinnerData = fetchedData.find(
          (a) => a.date === dateString && a.mealType === 'dinner'
        );
        mergedAvailability.push({
          date: dateString,
          mealType: 'dinner' as const,
          isPresent: dinnerData ? dinnerData.isPresent : defaultPresent,
        });
      }

      setLocalAvailability(mergedAvailability);
      setHasChanges(false);
    }
  }, [data, weekStart, user.defaultAvailability, user.id]);

  const handleAvailabilityChange = (newAvailability: BulkAvailabilityItem[]) => {
    setLocalAvailability(newAvailability);
    setHasChanges(true);
  };

  const handleSave = () => {
    bulkUpdateMutation.mutate(
      {
        userId: user.id,
        availability: localAvailability,
      },
      {
        onSuccess: () => {
          setHasChanges(false);
        },
      }
    );
  };

  const handleDiscard = () => {
    if (data?.availability) {
      const fetchedData = data.availability;
      const mergedAvailability: BulkAvailabilityItem[] = [];

      // Restore all dates with smart defaults for missing days
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStart, i);
        const dateString = formatDateForAPI(date);

        // Use user's default availability preference
        const defaultPresent = user.defaultAvailability || false;

        // Check if lunch data exists for this date
        const lunchData = fetchedData.find(
          (a) => a.date === dateString && a.mealType === 'lunch'
        );
        mergedAvailability.push({
          date: dateString,
          mealType: 'lunch' as const,
          isPresent: lunchData ? lunchData.isPresent : defaultPresent,
        });

        // Check if dinner data exists for this date
        const dinnerData = fetchedData.find(
          (a) => a.date === dateString && a.mealType === 'dinner'
        );
        mergedAvailability.push({
          date: dateString,
          mealType: 'dinner' as const,
          isPresent: dinnerData ? dinnerData.isPresent : defaultPresent,
        });
      }

      setLocalAvailability(mergedAvailability);
      setHasChanges(false);
    }
  };

  return (
    <div
      className={`rounded-lg p-6 mb-8 ${
        isOwnAvailability
          ? 'bg-blue-50 border-2 border-blue-500'
          : 'bg-gray-50 border border-gray-300'
      }`}
    >
      <h2 className="text-2xl font-semibold mb-4 text-gray-900">
        {user.name}
        {isOwnAvailability && <span className="text-blue-600"> (vous)</span>}
      </h2>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          {Array.from({ length: 7 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-4 h-40 animate-pulse"
            >
              <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4" />
              <div className="space-y-3">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Week calendar */}
      {!isLoading && (
        <WeekCalendar
          weekStart={weekStart}
          availability={localAvailability}
          onChange={handleAvailabilityChange}
        />
      )}

      {/* Success message */}
      {bulkUpdateMutation.isSuccess && !hasChanges && (
        <div className="mt-4 rounded-md bg-green-50 p-4" role="alert">
          <div className="flex">
            <svg
              className="h-5 w-5 text-green-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {t('availability.successMessage')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save/Discard buttons */}
      {hasChanges && (
        <div className="mt-6 flex items-center justify-end gap-4 bg-white rounded-lg shadow-md p-4">
          <p className="text-sm text-gray-600 mr-auto">{t('availability.unsavedChanges')}</p>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={bulkUpdateMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('availability.discardChanges')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={bulkUpdateMutation.isPending}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-700 rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {bulkUpdateMutation.isPending ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('availability.saving')}
              </span>
            ) : (
              t('availability.saveChanges')
            )}
          </button>
        </div>
      )}
    </div>
  );
}
